import { RSSFeed, RSSItem } from '../types/rss';

interface CachedFeed {
  id: string;
  feed: RSSFeed;
  timestamp: number;
  url: string;
}

class IndexedDBService {
  private dbName = 'RSSFeedCache';
  private version = 1;
  private storeName = 'feeds';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('Erro ao abrir IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Criar object store se não existir
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('url', 'url', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  async saveFeed(url: string, feed: RSSFeed): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const cachedFeed: CachedFeed = {
        id: this.generateId(url),
        feed,
        timestamp: Date.now(),
        url
      };

      const request = store.put(cachedFeed);

      request.onsuccess = () => {
        console.log('Feed salvo no cache local:', url);
        resolve();
      };

      request.onerror = () => {
        console.error('Erro ao salvar feed no cache:', request.error);
        reject(request.error);
      };
    });
  }

  async getFeed(url: string, maxAge: number = 5 * 60 * 1000): Promise<RSSFeed | null> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(this.generateId(url));

      request.onsuccess = () => {
        const result = request.result as CachedFeed | undefined;
        
        if (!result) {
          resolve(null);
          return;
        }

        // Verifica se o cache ainda é válido
        const isExpired = Date.now() - result.timestamp > maxAge;
        
        if (isExpired) {
          console.log('Cache expirado para:', url);
          resolve(null);
        } else {
          console.log('Feed carregado do cache local:', url);
          resolve(result.feed);
        }
      };

      request.onerror = () => {
        console.error('Erro ao buscar feed no cache:', request.error);
        reject(request.error);
      };
    });
  }

  async clearExpiredFeeds(maxAge: number = 24 * 60 * 60 * 1000): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      const cutoffTime = Date.now() - maxAge;
      
      const request = index.openCursor(IDBKeyRange.upperBound(cutoffTime));
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          console.log('Cache limpo - feeds expirados removidos');
          resolve();
        }
      };

      request.onerror = () => {
        console.error('Erro ao limpar cache:', request.error);
        reject(request.error);
      };
    });
  }

  async getAllFeeds(): Promise<CachedFeed[]> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        console.error('Erro ao buscar todos os feeds:', request.error);
        reject(request.error);
      };
    });
  }

  private generateId(url: string): string {
    return btoa(url).replace(/[^a-zA-Z0-9]/g, '');
  }

  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Instância singleton
export const indexedDBService = new IndexedDBService();
export default indexedDBService;